import React, {useContext, useEffect, useState} from 'react';
import {Modal, Image} from 'react-native';
import Button from '../components/Button';
import CodeVerificationInput from '../components/CodeVerificationInput';
import ServiceClient from "../utils/ServiceClient";
import {StoreContext} from "../components/StoreContextProvider";
import MainStyle from "../../MainStyle";
import bg from '../assets/img/Group 2494.png';
import Div from "../lib/Div";
import {colors} from "../assets/styles/colors";
import P from "../lib/P";

type Props = {
  visible: boolean, setVisible: (code?: string) => void, email: string, phone?: string, goBackBtn?: boolean,
  onSuccess: (email: string) => void, isLoginPage?: boolean
};

const OtpModal = (props: Props) => {
  const {visible, email, phone, goBackBtn, isLoginPage} = props;
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const Context = useContext(StoreContext);
  const [isCodeWasResend, setIsCodeWasResend] = useState(false);
  const [bgColor, setBgColor] = useState('transparent');

  const otpLogin = () => {
    Context.setLoadingOverlay(true);

    Context.otpLogin(email, code).then(() => {
      Context.setLoadingOverlay(false);
      props.setVisible();
      props.onSuccess(email);

    }).catch((err) => {
      Context.setLoadingOverlay(false);
      console.log('otpLogin err', err);

      if (err.code === 10010008 && err.message === 'User login rejected') {
        setErrorMessage('Your email is not associated with a Player Account. Please create an account or edit login details.');

      } else if (err.code === 10010009 && err.message === 'User login rejected') {
        setErrorMessage('The Pin Code entered does not match your User Account. Please re-enter or select the “Forgot Password” button to reset.');

      } else if (err?.message) {
        setErrorMessage(err.message);
      }
    });
  };

  const requestOtpCode = () => {
    Context.setLoadingOverlay(true);
    const payload = phone ? {phone} : {email};

    ServiceClient.requestOtpCode(payload).then(() => {
      Context.setLoadingOverlay(false);
      setCode('');
      setErrorMessage('');
      setIsCodeWasResend(true);

    }).catch(err => {
      console.log('requestOtpCode err', err);
      Context.setLoadingOverlay(false);

      if (err && err.message) setErrorMessage(err.message || 'Code expired or incorrect');
    });
  };

  const verifyOtpCode = () => {
    Context.setLoadingOverlay(true);

    ServiceClient.verifyOtpCode(code, email, phone).then(codeIsCorrect => {
      Context.setLoadingOverlay(false);

      if (codeIsCorrect) props.setVisible(code);
      else setErrorMessage('Code expired or incorrect');

    }).catch(err => {
      Context.setLoadingOverlay(false);
      console.log('verifyOtpCode err', err);
      if (err && err.message) setErrorMessage(err.message || 'Code expired or incorrect');
    });
  };

  const submit = () => {
    if (isLoginPage) otpLogin();
    else verifyOtpCode();
  };

  useEffect(() => {
    if (visible) {
      setCode('');
      setErrorMessage('');
      setIsCodeWasResend(false);

      const timeout = setTimeout(() => {
        setBgColor(colors.popupDropBg);
      }, 400);

      return () => clearTimeout(timeout);

    } else {
      setBgColor('transparent');
      setCode('');
      setErrorMessage('');
      setIsCodeWasResend(false);
    }
    return undefined;
  }, [email, phone, visible]);

  useEffect(() => {
    if (isCodeWasResend) {
      const timeout = setTimeout(() => {
        setIsCodeWasResend(false);
      }, 3000);
      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [isCodeWasResend]);

  useEffect(() => {
    if (errorMessage) setErrorMessage('');
  }, [code]);

  return (
    <Modal animationType="slide" transparent visible={visible} onRequestClose={() => props.setVisible()}>
      <Div flex={1} zIndex={2} bg={bgColor}>
        <Div flex={1}/>

        <Div w="100%" maxW={430} mx="auto" position="relative" bg={colors.loading_login} borderHorizontalWidth={1}
             borderColor={colors.settings_border}>
          <Image source={bg as any} style={styles.image} resizeMode="center"/>

          <P ta="center" fs={18} fw="500" mb={16}>{`We've sent you a code to your phone\nfor verification.`}</P>

          {!!errorMessage ? (
            <P accessibilityRole="alert" accessibilityLiveRegion="assertive" ta="center" fs={18} fw="700" mb={24}
               color={colors.newDangerText}>
              {errorMessage}
            </P>
          ) : isCodeWasResend ? (
            <P accessibilityRole="alert" accessibilityLiveRegion="polite" ta="center" fs={18} fw="700" mb={24}
               color={colors.darkYellow}>
              Code was resent
            </P>
          ) : (
            <P ta="center" fs={18} fw="700" mb={24}>Please enter the code below:</P>
          )}

          <CodeVerificationInput value={code} onChange={setCode}/>

          <Div px={18} mb={20}>
            <Button text="Submit" onPress={submit} disabled={code.length !== 6} w="100%"/>
          </Div>

          <Div px={20} row mb={60}>
            <Button
              text="Resend Code" bg="transparent" borderWidth={1} borderColor="white" onPress={requestOtpCode}
              style={MainStyle.flex1} color="white"
            />

            {goBackBtn && (
              <>
                <Div w={10}/>
                <Button
                  text="Back" bg="transparent" borderWidth={1} borderColor="white" style={MainStyle.flex1}
                  onPress={() => props.setVisible()} color="white"
                />
              </>
            )}
          </Div>
        </Div>
      </Div>
    </Modal>
  );
};

const styles = {
  image: [MainStyle.h_368, MainStyle.absolute, MainStyle.t__70, MainStyle.z_i__1, MainStyle.l__1, MainStyle.r__1]
};

export default React.memo(OtpModal);
